import React, { useCallback, useEffect, useState } from "react";
import styled from "styled-components";
import { toast } from "react-toastify";
import {
  FaGift,
  FaUserFriends,
  FaCheckCircle,
  FaClock,
  FaCopy,
  FaWhatsapp,
} from "react-icons/fa";

import { API_BASE } from "../config/constants";

const ReferralPage = () => {
  const user = JSON.parse(localStorage.getItem("userData") || "{}")?.data;
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({
    referral_code: "",
    share_message: "",
    stats: {},
    rewards: [],
    pending_friends: [],
  });

  const loadInfo = useCallback(async () => {
    if (!user?.id) {
      toast.error("User not found. Please login again.");
      return;
    }
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE}/referral/info`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ restaurantid: user.id }),
      });
      const json = await response.json().catch(() => ({}));
      if (!response.ok || !json?.success) {
        throw new Error(json?.message || "Failed to load referral info");
      }
      setData({
        referral_code: json?.referral_code || "",
        share_message: json?.share_message || "",
        stats: json?.stats || {},
        rewards: Array.isArray(json?.rewards) ? json.rewards : [],
        pending_friends: Array.isArray(json?.pending_friends) ? json.pending_friends : [],
      });
    } catch (error) {
      toast.error(error?.message || "Failed to load referral info");
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    loadInfo();
  }, [loadInfo]);

  const shareNow = async () => {
    const text = data?.share_message || "Join Bill Sarthi";
    try {
      if (navigator.share) {
        await navigator.share({ text });
      } else {
        await navigator.clipboard.writeText(text);
        toast.success("Referral message copied");
      }
    } catch {
      toast.info("Sharing cancelled");
    }
  };

  const copyCode = async () => {
    if (!data.referral_code) return;
    try {
      await navigator.clipboard.writeText(data.referral_code);
      toast.success("Referral code copied");
    } catch {
      toast.error("Unable to copy referral code");
    }
  };

  const redeem = async (reward, rewardType) => {
    try {
      const payload = {
        restaurantid: user.id,
        reward_id: reward.id,
        reward_type: rewardType,
      };
      if (rewardType === "upi") {
        const upi = window.prompt("Enter UPI ID");
        if (!upi) return;
        payload.upi_vpa = upi.trim();
      }

      const response = await fetch(`${API_BASE}/referral/redeem`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const json = await response.json().catch(() => ({}));
      if (!response.ok || !json?.success) {
        throw new Error(json?.message || "Failed to redeem reward");
      }
      toast.success(json?.message || "Reward redeemed");
      loadInfo();
    } catch (error) {
      toast.error(error?.message || "Failed to redeem reward");
    }
  };

  return (
    <Page>
      <Hero>
        <HeroContent>
          <Eyebrow><FaGift /> Referral rewards</Eyebrow>
          <h1>Invite restaurants.<br />Earn rewards together.</h1>
          <p>Share your unique code with restaurant owners and unlock rewards when they qualify.</p>
          <HeroActions>
            <ShareButton type="button" onClick={shareNow} disabled={loading || !data.share_message}>
              <FaWhatsapp /> Share invitation
            </ShareButton>
          </HeroActions>
        </HeroContent>
        <CodeBox>
          <CodeLabel>Your referral code</CodeLabel>
          <CodeRow>
            <Code>{data.referral_code || "—"}</Code>
            <CopyButton type="button" onClick={copyCode} disabled={!data.referral_code} aria-label="Copy referral code">
              <FaCopy />
            </CopyButton>
          </CodeRow>
          <CodeHint>Tap the copy button and share it anywhere.</CodeHint>
        </CodeBox>
      </Hero>

      <Stats>
        <Stat>
          <StatIcon $tone="blue"><FaUserFriends /></StatIcon>
          <StatText><span>Total referred</span><strong>{data?.stats?.total_referred ?? 0}</strong></StatText>
        </Stat>
        <Stat>
          <StatIcon $tone="green"><FaCheckCircle /></StatIcon>
          <StatText><span>Qualified referrals</span><strong>{data?.stats?.qualified_referred ?? 0}</strong></StatText>
        </Stat>
        <Stat>
          <StatIcon $tone="amber"><FaClock /></StatIcon>
          <StatText><span>Pending rewards</span><strong>{data?.stats?.pending_rewards ?? 0}</strong></StatText>
        </Stat>
      </Stats>

      <ContentGrid>
      <Card>
        <CardHeading>
          <div><IconTitle><FaGift /></IconTitle><div><h3>Eligible Rewards</h3><p>Rewards ready for redemption appear here.</p></div></div>
        </CardHeading>
        {loading ? (
          <Empty><EmptyIcon><FaGift /></EmptyIcon><strong>Loading rewards...</strong></Empty>
        ) : data.rewards.length === 0 ? (
          <Empty><EmptyIcon><FaGift /></EmptyIcon><strong>No rewards available yet</strong><span>Invite a restaurant to start earning rewards.</span></Empty>
        ) : (
          data.rewards.map((reward) => (
            <Reward key={reward.id}>
              <div>
                <strong>{reward?.referee_name || "Friend"}</strong>
                <small>Status: {reward?.reward_status || "unknown"}</small>
              </div>
              {reward?.reward_status === "eligible" && !reward?.reward_type ? (
                <ActionRow>
                  <SmallBtn type="button" onClick={() => redeem(reward, "upi")}>
                    <FaGift /> Redeem UPI
                  </SmallBtn>
                  <SmallBtn type="button" onClick={() => redeem(reward, "starter_3m")}>
                    <FaGift /> Redeem Plan
                  </SmallBtn>
                </ActionRow>
              ) : (
                <small>{reward?.reward_type || "No action"}</small>
              )}
            </Reward>
          ))
        )}
      </Card>

      <Card>
        <CardHeading>
          <div><IconTitle><FaUserFriends /></IconTitle><div><h3>Pending Friends</h3><p>Track invited restaurants waiting to qualify.</p></div></div>
        </CardHeading>
        {loading ? (
          <Empty><EmptyIcon><FaUserFriends /></EmptyIcon><strong>Loading invitations...</strong></Empty>
        ) : data.pending_friends.length === 0 ? (
          <Empty><EmptyIcon><FaUserFriends /></EmptyIcon><strong>No pending invitations</strong><span>Your shared invitations will appear here.</span></Empty>
        ) : (
          data.pending_friends.map((friend) => (
            <Friend key={friend.id || friend.referee_name}>
              <span>{friend?.referee_name || "-"}</span>
              <small>{friend?.status || "pending"}</small>
            </Friend>
          ))
        )}
      </Card>
      </ContentGrid>
    </Page>
  );
};

export default ReferralPage;

const Page = styled.div`
  min-height: 100vh;
  padding: 24px;
  background: #f4f7fb;
  display: grid;
  align-content: start;
  gap: 18px;
  color: #0f172a;
  @media (max-width: 700px) { padding: 14px; gap: 14px; }
`;

const Hero = styled.div`
  position: relative;
  overflow: hidden;
  background: linear-gradient(135deg, #075f9e 0%, #087fc0 58%, #09a0d1 100%);
  color: #fff;
  border-radius: 24px;
  padding: 30px 34px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 28px;
  box-shadow: 0 16px 36px rgba(1, 91, 154, 0.2);
  &::after { content: ""; position: absolute; width: 300px; height: 300px; border-radius: 50%; right: -90px; top: -150px; background: rgba(255,255,255,.09); }
  h1 { margin: 10px 0 8px; font-size: clamp(1.75rem, 3vw, 2.5rem); line-height: 1.12; }
  p { margin: 0; max-width: 600px; color: rgba(255,255,255,.84); line-height: 1.55; }
  @media (max-width: 760px) { padding: 24px 20px; flex-direction: column; align-items: stretch; }
`;

const HeroContent = styled.div`position: relative; z-index: 1;`;
const Eyebrow = styled.div`display: inline-flex; align-items: center; gap: 8px; font-size: .78rem; font-weight: 800; text-transform: uppercase; letter-spacing: .08em; color: #dff6ff;`;
const HeroActions = styled.div`display: flex; gap: 10px; margin-top: 20px;`;
const ShareButton = styled.button`
  border: 0; border-radius: 12px; min-height: 44px; padding: 0 18px; background: #fff; color: #07679f;
  display: inline-flex; align-items: center; gap: 9px; font-size: .95rem; font-weight: 800; cursor: pointer;
  box-shadow: 0 7px 18px rgba(0,0,0,.12); &:disabled { opacity: .6; cursor: not-allowed; }
`;

const CodeBox = styled.div`
  position: relative; z-index: 1; width: min(100%, 330px); flex: 0 0 auto; padding: 18px;
  background: rgba(255,255,255,.14); border: 1px solid rgba(255,255,255,.3); border-radius: 18px;
  backdrop-filter: blur(8px);
`;
const CodeLabel = styled.div`font-size: .75rem; font-weight: 800; text-transform: uppercase; letter-spacing: .08em; color: rgba(255,255,255,.75);`;
const CodeRow = styled.div`display: flex; align-items: center; gap: 10px; margin: 10px 0 7px;`;

const Code = styled.div`
  flex: 1; border: 1px dashed rgba(255,255,255,.65); color: #fff; background: rgba(5,57,95,.22);
  border-radius: 11px; padding: 11px 14px; font-size: 1.15rem; font-weight: 900; letter-spacing: .08em;
`;
const CopyButton = styled.button`width: 44px; height: 44px; border: 0; border-radius: 11px; background: #fff; color: #076aa5; cursor: pointer; font-size: 1rem; &:disabled { opacity: .55; }`;
const CodeHint = styled.div`font-size: .75rem; color: rgba(255,255,255,.72);`;

const Stats = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 14px;
`;

const Stat = styled.div`
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  padding: 18px;
  display: flex; align-items: center; gap: 14px;
  box-shadow: 0 6px 18px rgba(15,23,42,.045);
`;
const StatIcon = styled.div`
  width: 46px; height: 46px; border-radius: 13px; display: grid; place-items: center; font-size: 1.1rem;
  color: ${({$tone}) => $tone === "green" ? "#16845b" : $tone === "amber" ? "#c17608" : "#0872ae"};
  background: ${({$tone}) => $tone === "green" ? "#e8f8f0" : $tone === "amber" ? "#fff5df" : "#e8f5fc"};
`;
const StatText = styled.div`span { display: block; color: #64748b; font-size: .8rem; font-weight: 700; } strong { display: block; margin-top: 2px; font-size: 1.55rem; }`;
const ContentGrid = styled.div`display: grid; grid-template-columns: repeat(2, minmax(0,1fr)); gap: 16px; @media (max-width: 850px) { grid-template-columns: 1fr; }`;

const Card = styled.div`
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 18px;
  padding: 20px;
  min-height: 245px;
  box-shadow: 0 6px 18px rgba(15,23,42,.04);
`;
const CardHeading = styled.div`
  margin-bottom: 15px; padding-bottom: 14px; border-bottom: 1px solid #e8eef5;
  > div { display: flex; align-items: center; gap: 11px; }
  h3 { margin: 0; font-size: 1.05rem; } p { margin: 3px 0 0; color: #718096; font-size: .8rem; }
`;
const IconTitle = styled.div`width: 38px; height: 38px; flex: 0 0 auto; border-radius: 11px; display: grid; place-items: center; color: #0872ae; background: #e8f5fc;`;

const Reward = styled.div`
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 10px;
  margin-bottom: 8px;
  display: flex;
  justify-content: space-between;
  gap: 10px;
  strong { display: block; color: #0f172a; }
  small { color: #64748b; }
`;

const ActionRow = styled.div`
  display: flex;
  gap: 6px;
`;

const SmallBtn = styled.button`
  border: 1px solid #cbd5e1;
  background: #fff;
  border-radius: 8px;
  height: 32px;
  padding: 0 10px;
  display: inline-flex;
  gap: 6px;
  align-items: center;
  font-weight: 700;
  cursor: pointer;
`;

const Friend = styled.div`
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 8px 10px;
  margin-bottom: 8px;
  display: flex;
  justify-content: space-between;
`;

const Empty = styled.div`
  min-height: 145px; display: flex; flex-direction: column; align-items: center; justify-content: center; text-align: center;
  color: #64748b; gap: 5px; strong { color: #334155; font-size: .92rem; } span { font-size: .78rem; }
`;
const EmptyIcon = styled.div`width: 48px; height: 48px; border-radius: 50%; display: grid; place-items: center; margin-bottom: 4px; color: #8abbd5; background: #eff8fd;`;
